import React, {
  ForwardRefExoticComponent,
  RefAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  OrderRollingOfferEvents,
  RollingOfferProps,
  SubRollingOfferProps,
} from '../shared/types';
import {
  StyledHugeRollingOffer,
  StyledHugeRollingOfferContainer,
  StyledHugeRollingOfferWrapper,
  StyledRollingOfferScroller,
} from './style';
import {
  arrowHeightWidth,
  containerPadding,
  maxHeight,
  rollingOfferUtils,
  subOfferHeight,
} from '../shared/utils';
import { AnimatePresence } from 'framer-motion';
import { HugeSubRollingOfferWrapper } from './components/SubWrapper';
import { ArrowWrapper } from './components/ArrowWrapper';
import { Banner } from 'components/Banner';
import { useIsDesktop } from 'utils/index';

const DESKTOP_BREAKPOINT = 1264;
const SMALL_DESKTOP_VISIBLE_OFFERS = 3;
const LARGE_DESKTOP_VISIBLE_OFFERS = 5;

export const HugeRollingOffer: ForwardRefExoticComponent<
  RollingOfferProps & RefAttributes<OrderRollingOfferEvents>
> = React.forwardRef<OrderRollingOfferEvents, RollingOfferProps>(
  (
    {
      offerId,
      backgroundImage,
      backgroundOpacity = 80,
      headerImage,
      backgroundColor = { colorOne: '#272727' },
      borderColor = { colorOne: '#FFCC4D', colorTwo: '#ffff' },
      borderWidth = 0,
      template,
      indexToCollect,
      collectBehavior,
      arrowColor = '#ffffff',
      subRollingOffersProducts,
      subRollingOfferUiProps,
      selectOffer,
      isPreview = false,
      timer,
      isPermanent,
      offerData,
      isHorizontalViewEnabled,
    },
    ref,
  ) => {
    const elementRef = useRef<HTMLDivElement | null>(null);
    const subOffersWrapperRef = useRef<HTMLDivElement>(null);
    const isHorizontalViewActive = useIsDesktop(848) && isHorizontalViewEnabled;
    const [isAnimatingArrow, setIsAnimatingArrow] = useState(false);
    const [isAnimatingOffer, setIsAnimatingOffer] = useState(false);
    const [subOffers, setSubOffers] = useState<SubRollingOfferProps[]>([]);
    const [subOfferWidth, setSubOfferWidth] = useState(0);
    const [height, setHeight] = useState<number>(0);
    const [showRightArrow, setShowRightArrow] = useState(
      isHorizontalViewActive,
    );
    const [showLeftArrow, setShowLeftArrow] = useState(false);

    useEffect(() => {
      if (isAnimatingOffer || !subOffers?.length) return;

      const newHeight =
        Math.min(
          Math.ceil(subOffers?.length / 2) *
            (subOfferHeight + arrowHeightWidth) -
            arrowHeightWidth +
            containerPadding * 2,
          maxHeight - 4,
        ) + 5;

      setHeight(newHeight);
    }, [subOffers?.length, isAnimatingOffer]);

    useEffect(() => {
      setSubOffers(
        rollingOfferUtils.getSubRollingOffersFromProducts({
          indexToCollect,
          subRollingOffersProducts,
          subRollingOfferUiProps,
          collectBehavior,
          selectOffer: () => selectOffer(),
        }),
      );
    }, [indexToCollect, subRollingOffersProducts, subRollingOfferUiProps]);

    const scrollToStart = () => {
      if (subOffersWrapperRef.current) {
        const container = subOffersWrapperRef.current;
        container.scrollTo({
          left: 0,
          behavior: 'smooth',
        });
        setShowRightArrow(isHorizontalViewActive);
        setShowLeftArrow(false);
      }
    };

    const scrollToSixth = () => {
      if (subOffersWrapperRef.current) {
        const container = subOffersWrapperRef.current;
        const targetChild = container.children[LARGE_DESKTOP_VISIBLE_OFFERS];
        if (targetChild) {
          targetChild.scrollIntoView({
            behavior: 'smooth',
            inline: 'start',
            block: 'nearest',
          });
          setShowRightArrow(false);
          setShowLeftArrow(isHorizontalViewActive);
        }
      }
    };

    useImperativeHandle(ref, () => ({
      onOrderSuccess,
      onOrderFail,
    }));

    const onOrderSuccess = () => {
      setIsAnimatingArrow(true);
      setIsAnimatingOffer(true);
    };

    const onOrderFail = () => {
      setIsAnimatingArrow(false);
      setIsAnimatingOffer(false);
    };

    useEffect(() => {
      const updateOnResize = () => {
        const screenWidth = window.innerWidth;
        const visibleOffers =
          screenWidth >= DESKTOP_BREAKPOINT
            ? LARGE_DESKTOP_VISIBLE_OFFERS
            : SMALL_DESKTOP_VISIBLE_OFFERS;
        const shouldShowRightArrow = subOffers.length > visibleOffers;

        setShowRightArrow(
          isHorizontalViewActive ? shouldShowRightArrow : false,
        );
        setShowLeftArrow(false);
        if (elementRef.current) {
          const containerWidth =
            elementRef.current.offsetWidth -
            containerPadding * 2 -
            borderWidth * 2;

          setSubOfferWidth(
            isHorizontalViewActive
              ? 220
              : (containerWidth - arrowHeightWidth) / 2,
          );
        }
      };

      updateOnResize();

      window.addEventListener('resize', updateOnResize);
      return () => {
        window.removeEventListener('resize', updateOnResize);
      };
    }, [isHorizontalViewActive, subOffers.length]);

    return (
      <>
        {headerImage && (
          <Banner
            imageSrc={headerImage}
            maxWidth="300px"
            maxHeight="100px"
            mx="auto"
          />
        )}
        <StyledRollingOfferScroller className="slick-slider slick-initialized">
          {showRightArrow && (
            <button className="slick-arrow slick-next" onClick={scrollToSixth}>
              →
            </button>
          )}
          {showLeftArrow && (
            <button className="slick-arrow slick-prev" onClick={scrollToStart}>
              ←
            </button>
          )}
        </StyledRollingOfferScroller>
        <StyledHugeRollingOfferWrapper
          backgroundOpacity={backgroundOpacity / 100}
          headerImage={headerImage}
          backgroundImage={backgroundImage}
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          borderWidth={borderWidth}
          data-testid={offerId}
        >
          {timer && !isPreview && (
            <div className="timer">{timer && `Ends in ${timer}`}</div>
          )}
          <StyledHugeRollingOfferContainer
            ref={elementRef}
            isPreview={isPreview}
            className={`rolling-offer-container ${template}`}
            isHorizontalViewEnabled={isHorizontalViewActive}
            hasTimer={!isPermanent && !isPreview}
          >
            <StyledHugeRollingOffer
              className={'rolling-offer'}
              height={height}
              isScrollable={isPreview && subOffers?.length > 6}
              hasTimer={!isPermanent && !isPreview}
              ref={subOffersWrapperRef}
              isHorizontalViewEnabled={isHorizontalViewActive}
            >
              {subOfferWidth && (
                <AnimatePresence
                  onExitComplete={() => setIsAnimatingOffer(false)}
                >
                  {subOffers.map((subOffer, index) => (
                    <React.Fragment key={subOffer.index}>
                      <HugeSubRollingOfferWrapper
                        index={index}
                        parentData={offerData}
                        subOffer={subOffer}
                        isAnimating={
                          (isAnimatingArrow || isAnimatingOffer) && !isPreview
                        }
                        width={subOfferWidth}
                        isPreview={isPreview}
                        isHorizontalViewActive={isHorizontalViewActive}
                      />
                      {isHorizontalViewActive &&
                        index < subOffers.length - 1 && (
                          <AnimatePresence
                            onExitComplete={() => setIsAnimatingArrow(false)}
                          >
                            <ArrowWrapper
                              index={4}
                              key={index}
                              arrowColor={arrowColor}
                              isHorizontalViewActive={isHorizontalViewActive}
                            />
                          </AnimatePresence>
                        )}
                    </React.Fragment>
                  ))}
                </AnimatePresence>
              )}
              {!isHorizontalViewActive && (
                <AnimatePresence
                  onExitComplete={() => setIsAnimatingArrow(false)}
                >
                  {Array.from({ length: subOffers.length - 1 }, (_, index) => (
                    <ArrowWrapper
                      index={index}
                      key={index}
                      arrowColor={arrowColor}
                      isHorizontalViewActive={isHorizontalViewActive}
                    />
                  ))}
                </AnimatePresence>
              )}
            </StyledHugeRollingOffer>
          </StyledHugeRollingOfferContainer>
        </StyledHugeRollingOfferWrapper>
      </>
    );
  },
);
